import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {Validators, FormGroup, FormControl, ReactiveFormsModule} from '@angular/forms';
import {WebService} from '../services/web/web.service';
import {ToastService} from '../services/notification/toast.service';
import {SpinnerService} from '../services/spinner/spinner.service';
import {AuthService} from 'src/app/services/auth/auth.service';
import {SecurityService} from "../services/security.service";
import {LoggerService} from '../services/logger.service';
import {EnhancedAuthService} from '../services/auth/enhanced-auth.service';
import {GoogleSignInService} from '../services/auth/google-signin.service';
import {InactivityMonitorService} from '../services/inactivity-monitor.service';
import {WebAuthnService} from '../services/webauthn.service';
import {PasskeyService} from '../services/passkey.service';
import {PasskeyErrorHandlerService} from '../services/passkey-error-handler.service';
import {PasskeyAvailabilityState} from '../models/passkey.model';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {
    AuthType,
    IStandardAuthCredentials,
    IGoogleAuthCredentials
} from '../models/auth.models';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
    signinForm: FormGroup;
    passwordFieldType: string = 'password';

    // 👇 añadidos para el flujo admin
    modoSeleccionPortal: boolean = false;
    portals: any[] = [];

    // ═══════════════════════════════════════════════════════════════════
    // PROPIEDADES PARA PASSKEY
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Indica si el usuario tiene passkey registrada
     */
    hasPasskey: boolean = false;

    /**
     * Indica si se está verificando la disponibilidad de passkey
     */
    isCheckingPasskey: boolean = false;

    /**
     * Indica si se está procesando el login con passkey
     */
    isLoadingPasskey: boolean = false;

    /**
     * Mensaje de error si falla el login con passkey
     */
    passkeyError: string | null = null;

    /**
     * Nombre del método biométrico disponible
     */
    biometricMethodName: string = 'Passkey';

    /**
     * Indica si el navegador soporta WebAuthn
     */
    webAuthnSupported: boolean = false;

    /**
     * Indica qué método de login está activo
     * 'passkey' = Muestra opción de passkey
     * 'standard' = Muestra formulario tradicional
     */
    activeLoginMethod: 'passkey' | 'standard' = 'standard';
    passkeyAvailabilityState: PasskeyAvailabilityState = 'idle';
    private passkeyAbortController: AbortController | null = null;
    private passkeyGeneration = 0;
    private passkeyLookupAbortController: AbortController | null = null;
    private passkeyLookupGeneration = 0;
    private readonly passkeyOperationTimeoutMs = 90000;
    private readonly usernameChanges = new Subscription();

    constructor(
        private securityService: SecurityService,
        private router: Router,
        private spinner: SpinnerService,
        private webService: WebService,
        private toast: ToastService,
        private auth: AuthService,
        private logger: LoggerService,
        private enhancedAuthService: EnhancedAuthService,
        private googleSignInService: GoogleSignInService,
        private inactivityMonitor: InactivityMonitorService,
        private webAuthnService: WebAuthnService,
        private passkeyService: PasskeyService,
        private translate: TranslateService,
        private passkeyErrorHandler: PasskeyErrorHandlerService
    ) {
        this.signinForm = new FormGroup({
            username: new FormControl('', [Validators.required, Validators.email]),
            password: new FormControl('', Validators.required)
        });
    }

    ngOnInit(): void {
        this.usernameChanges.add(
            this.signinForm.get('username')!.valueChanges.subscribe(() => this.cancelPasskeyOperation())
        );
        this.logger.log('🔵 [LOGIN] Componente de login inicializado');

        // Envía un mensaje al parent cuando se carga el componente
        if (window.parent !== window) {
            window.parent.postMessage({ status: 'loaded', url: window.location.href }, '*');
        }

        // Verificar si el usuario acaba de hacer logout
        const justLoggedOut = sessionStorage.getItem('just_logged_out');
        if (justLoggedOut === 'true') {
            this.logger.log('🚪 [LOGIN] Usuario acaba de hacer logout - NO auto-login con Google');
            sessionStorage.removeItem('just_logged_out');
            
            // NO suscribirse a Google Auth para evitar login automático
            this.logger.log('ℹ️ [LOGIN] Mostrando formulario de login sin auto-login');
            
            // Verificar soporte de WebAuthn
            this.webAuthnSupported = this.webAuthnService.isWebAuthnSupported();
            this.logger.log('🔍 [LOGIN] Soporte de WebAuthn:', this.webAuthnSupported ? '✅' : '❌');
            return;
        }

        // Verificar si ya existe una sesión activa
        this.logger.log('🔍 [LOGIN] Verificando si existe sesión activa...');
        const token = this.securityService.getToken();
        const refreshToken = this.securityService.getRefreshToken();
        const isPinValidated = this.securityService.getPinValidationStatus();

        // ✅ NUEVA LÓGICA: Verificar si el refresh token también está expirado
        let isRefreshTokenExpired = false;
        if (refreshToken) {
            try {
                const refreshPayload = JSON.parse(atob(refreshToken.split('.')[1]));
                const currentTime = Math.floor(new Date().getTime() / 1000);
                const expirationTime = refreshPayload.exp;
                isRefreshTokenExpired = expirationTime < currentTime;
            } catch {
                isRefreshTokenExpired = true;
            }
        }

        // Si hay tokens pero el refresh token está expirado, limpiar sesión
        if (token && refreshToken && isRefreshTokenExpired) {
            this.logger.log('🧹 [LOGIN] Limpiando localStorage y mostrando formulario de login');
            
            // Limpiar localStorage sin hacer logout completo (para evitar reload)
            localStorage.clear();
            
            // Mostrar formulario de login
            this.subscribeToGoogleAuth();
            return;
        }

        // Si hay refresh token válido, confiar en que el sistema renovará el JWT automáticamente
        if (token && refreshToken && !isRefreshTokenExpired) {
            this.logger.log('✅ [LOGIN] Sesión activa detectada');
            this.logger.log('   - PIN validado:', isPinValidated ? '✅' : '❌ (se validará después)');
            
            if (isPinValidated) {
                // Si el PIN ya está validado, ir directo a my-portals
                // El AuthGuard renovará el token si está expirado
                this.logger.log('🚀 [LOGIN] PIN ya validado - Redirigiendo a /my-portals');
                this.router.navigate(['/my-portals']);
            } else {
                // Si el PIN NO está validado, ir a /validate
                this.logger.log('🚀 [LOGIN] PIN pendiente de validación - Redirigiendo a /validate');
                this.router.navigate(['/validate']);
            }
            return;
        }

        // Si no hay sesión activa, mostrar formulario de login
        this.logger.log('ℹ️ [LOGIN] No hay sesión activa - Mostrando formulario de login');
        this.logger.log('   - PIN validado:', isPinValidated);

        // Escuchar cambios en el estado de autenticación de Google
        this.subscribeToGoogleAuth();

        // Verificar soporte de WebAuthn
        this.webAuthnSupported = this.webAuthnService.isWebAuthnSupported();
        this.logger.log('🔍 [LOGIN] Soporte de WebAuthn:', this.webAuthnSupported ? '✅' : '❌');
    }

    ngOnDestroy(): void {
        this.cancelPasskeyOperation();
        this.usernameChanges.unsubscribe();
    }

    togglePasswordVisibility() {
        this.passwordFieldType =
            this.passwordFieldType === 'password' ? 'text' : 'password';
        this.logger.log('👁️ [LOGIN] Visibilidad de contraseña cambiada a:', this.passwordFieldType);
    }

    signin() {
        this.cancelPasskeyOperation();
        this.logger.log('🟢 [LOGIN-STANDARD] ========================================');
        this.logger.log('🟢 [LOGIN-STANDARD] Iniciando proceso de login ESTÁNDAR');
        this.logger.log('🟢 [LOGIN-STANDARD] Método: Usuario + Password');
        this.logger.log('📝 [LOGIN-STANDARD] Validando formulario...');

        if (this.signinForm.invalid) {
            this.logger.warn('⚠️ [LOGIN-STANDARD] Formulario inválido, no se puede continuar');
            return;
        }

        this.spinner.open();
        const signinData = this.signinForm.value;

        this.logger.log('🔑 [LOGIN-STANDARD] Password: ********');
        this.logger.log('📤 [LOGIN-STANDARD] Enviando credenciales al SecurityService...');
        this.logger.log('🔧 [LOGIN-STANDARD] Servicio usado: SecurityService (ORIGINAL)');

        this.securityService.login(signinData.username, signinData.password).subscribe({
            next: () => {
                this.logger.log('✅ [LOGIN-STANDARD] Login exitoso');
                this.spinner.close();

                this.logger.log('🚀 [LOGIN-STANDARD] Redirigiendo a /validate para validación de PIN');
                this.router.navigate(['/validate']);
            },
            error: (error) => {
                this.logger.error('❌ [LOGIN-STANDARD] Error en el login');
                this.spinner.close();
                
                // Extraer mensaje de error amigable
                const errorMessage = this.getErrorMessage(error);
                this.toast.showError(errorMessage);
            }
        });
    }

    goToLoginOnBoard() {
        this.router.navigate(['/login']);
    }

    goRegistry() {
        this.router.navigateByUrl('/create');
    }

    goHome() {
        this.router.navigate(['/']);
    }

    /**
     * Extrae un mensaje de error amigable para el usuario
     * @param error - Error recibido del backend o servicio
     * @returns Mensaje de error amigable
     */
    private getErrorMessage(error: any): string {
        this.logger.log('🔍 [LOGIN] Extrayendo mensaje de error amigable');
        
        // Si el error ya tiene un mensaje procesado (desde SecurityService)
        if (error?.message) {
            return error.message;
        }
        
        // Si es un error HTTP con estructura específica
        if (error?.error) {
            // Verificar si hay un mensaje específico del backend
            if (error.error.message) {
                // Personalizar mensajes específicos
                if (error.error.message.includes('not found')) {
                    return this.translate.instant('login.messages.userNotFound');
                }
                
                return error.error.message;
            }
            
            // Verificar por código de estado
            if (error.status) {
                switch (error.status) {
                    case 401:
                        return this.translate.instant('login.messages.invalidCredentials');
                    case 404:
                        return this.translate.instant('login.messages.userNotFound');
                    case 403:
                        return this.translate.instant('login.messages.accessDenied');
                    case 500:
                        return this.translate.instant('common.errors.serverError');
                    case 503:
                        return this.translate.instant('common.errors.serviceUnavailable');
                    case 0:
                        return this.translate.instant('common.errors.noConnection');
                }
            }
        }
        
        // Mensaje genérico si no se pudo determinar el error específico
        this.logger.warn('⚠️ [LOGIN] No se pudo determinar mensaje específico, usando genérico');
        return this.translate.instant('common.errors.generic');
    }

    /**
     * Suscribe al estado de autenticación de Google
     * Se ejecuta automáticamente cuando el usuario hace clic en el botón de Google
     */
    private subscribeToGoogleAuth(): void {
        this.logger.log('🔵 [LOGIN] Suscribiéndose al estado de autenticación de Google');

        this.googleSignInService.getAuthState().subscribe({
            next: (googleUser) => {
                if (googleUser) {
                    this.logger.log('✅ [LOGIN] Usuario autenticado con Google');
                    this.handleGoogleLogin(googleUser);
                }
            },
            error: () => {
                this.logger.error('❌ [LOGIN] Error en el estado de autenticación');
            }
        });
    }

    /**
     * Maneja el login con Google después de la autenticación exitosa
     */
    private handleGoogleLogin(googleUser: any): void {
        this.spinner.open();

        if (!googleUser.email || !googleUser.idToken) {
            this.spinner.close();
            this.toast.showError(this.translate.instant('login.messages.googleDataIncomplete'));
            return;
        }

        const credentials: IGoogleAuthCredentials = {
            username: googleUser.email,
            authType: AuthType.GOOGLE,
            googleToken: googleUser.idToken
        };

        this.enhancedAuthService.login(credentials).subscribe({
            next: (response) => {
                this.spinner.close();
                this.router.navigate(['/validate']);
            },
            error: (error) => {
                this.spinner.close();
                this.toast.showError(this.translate.instant('login.messages.googleAuthError'));
            }
        });
    }

    // ═══════════════════════════════════════════════════════════════════
    // MÉTODOS PARA PASSKEY
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Verifica si el usuario tiene passkey cuando ingresa su email
     * Se ejecuta cuando el usuario sale del campo de email (blur)
     */
    async checkPasskeyOnEmailBlur(): Promise<void> {
        this.logger.log('🔵 [LOGIN-PASSKEY] ========================================');
        this.logger.log('🔵 [LOGIN-PASSKEY] Evento blur ejecutado en campo de email');
        
        const email = this.signinForm.get('username')?.value;
        this.logger.log('📧 [LOGIN-PASSKEY] Email ingresado');

        // Validar que el email sea válido antes de verificar
        if (!email || this.signinForm.get('username')?.invalid) {
            this.logger.log('⚠️ [LOGIN-PASSKEY] Email inválido o vacío, no se verifica passkey');
            this.logger.log('   → Email vacío:', !email);
            this.logger.log('   → Email inválido:', this.signinForm.get('username')?.invalid);
            this.hasPasskey = false;
            this.passkeyAvailabilityState = 'idle';
            return;
        }

        // Verificar soporte de WebAuthn
        if (!this.webAuthnSupported) {
            this.logger.log('⚠️ [LOGIN-PASSKEY] WebAuthn no soportado');
            this.hasPasskey = false;
            this.passkeyAvailabilityState = 'idle';
            return;
        }

        this.logger.log('🔍 [LOGIN-PASSKEY] Verificando si el usuario tiene passkey...');
        this.logger.log('   → Email válido');
        const operation = this.startPasskeyLookup();
        const generation = this.passkeyLookupGeneration;
        this.isCheckingPasskey = true;
        this.passkeyAvailabilityState = 'checking';
        this.passkeyError = null;

        try {
            // Verificar si tiene passkey (partner es null en este portal)
            const hasPasskey = await this.passkeyService.hasPasskey(email, null as any, operation);
            if (!this.isCurrentPasskeyLookup(generation, email)) return;

            this.hasPasskey = hasPasskey;

            if (hasPasskey) {
                this.passkeyAvailabilityState = 'available';
                this.logger.log('✅ [LOGIN-PASSKEY] Usuario tiene passkey registrada');
                
                // Obtener nombre del método biométrico
                this.biometricMethodName = await this.webAuthnService.getBiometricMethodName();
                if (!this.isCurrentPasskeyLookup(generation, email)) return;
                this.logger.log('   → Método biométrico:', this.biometricMethodName);

                // Cambiar a modo passkey automáticamente
                this.activeLoginMethod = 'passkey';
                this.logger.log('🔄 [LOGIN-PASSKEY] Cambiando a modo passkey automáticamente');
            } else {
                this.passkeyAvailabilityState = 'absent';
                this.logger.log('ℹ️ [LOGIN-PASSKEY] Usuario NO tiene passkey registrada');
                this.activeLoginMethod = 'standard';
            }
        } catch (error: unknown) {
            if (!this.isCurrentPasskeyLookup(generation, email)) return;
            this.logger.error('❌ [LOGIN-PASSKEY] Error verificando passkey');
            this.hasPasskey = false;
            this.passkeyAvailabilityState = 'error';
            this.passkeyError = this.passkeyErrorHandler.handlePasskeyError(error, 'login').message;
        } finally {
            if (this.isCurrentPasskeyLookup(generation, email)) {
                this.isCheckingPasskey = false;
                this.passkeyLookupAbortController = null;
            }
        }
    }

    /**
     * Ejecuta el login con passkey
     */
    async loginWithPasskey(operation?: { signal: AbortSignal; deadlineAt: number }): Promise<void> {
        const email = this.signinForm.get('username')?.value;

        if (!email) {
            this.toast.showError(this.translate.instant('login.messages.emailRequired'));
            return;
        }

        this.logger.log('🔐 [LOGIN-PASSKEY] ========================================');
        this.logger.log('🔐 [LOGIN-PASSKEY] Iniciando login con Passkey');
        this.logger.log('   → Email disponible');

        if (this.isLoadingPasskey && !operation || operation?.signal.aborted) return;
        const currentOperation = operation ?? this.startPasskeyOperation();
        const generation = this.passkeyGeneration;
        this.isLoadingPasskey = true;
        this.passkeyError = null;

        try {
            this.logger.log('📡 [LOGIN-PASSKEY] Llamando a PasskeyService.loginWithPasskey()...');

            // Ejecutar login con passkey (partner es null en este portal)
            const response = await this.passkeyService.loginWithPasskey(email, null as any, currentOperation);
            if (!this.isCurrentPasskeyOperation(generation, email)) return;

            this.logger.log('✅ [LOGIN-PASSKEY] Login exitoso');

            if (response.jwt) {
                this.securityService.storeSession(response);
            }

            // ✅ IMPORTANTE: Marcar PIN como validado automáticamente
            // El usuario ya se autenticó con biométrico, no necesita validar PIN
            this.logger.log('✅ [LOGIN-PASSKEY] Marcando PIN como validado (autenticación biométrica)');
            this.securityService.isPinValidated(true);

            // ✅ Marcar que venimos de login con passkey
            // ValidatePinComponent usará esto para no mostrar formularios
            this.logger.log('🔐 [LOGIN-PASSKEY] Marcando flag de autenticación con passkey');
            sessionStorage.setItem('authenticated_with_passkey', 'true');

            // Mostrar mensaje de éxito
            this.toast.showSuccess(this.translate.instant('login.messages.welcomePasskey', {method: this.biometricMethodName}));

            // Redirigir a /validate para que ejecute su lógica (verificar admin, etc.)
            // pero sin mostrar formularios
            this.logger.log('🚀 [LOGIN-PASSKEY] Redirigiendo a /validate (sin mostrar formularios)');
            this.router.navigate(['/validate']);

        } catch (error: unknown) {
            if (!this.isCurrentPasskeyOperation(generation, email)) return;
            this.logger.error('❌ [LOGIN-PASSKEY] Error en login con passkey');

            const result = this.passkeyErrorHandler.handlePasskeyError(error, 'login');
            this.passkeyError = result.message;
            this.toast.showError(result.message);
        } finally {
            if (this.isCurrentPasskeyOperation(generation, email)) {
                this.isLoadingPasskey = false;
                this.passkeyAbortController = null;
            }
        }
    }

    /**
     * Cambia al método de login tradicional (email + password)
     */
    switchToStandardLogin(): void {
        this.cancelPasskeyOperation();
        this.logger.log('🔄 [LOGIN-PASSKEY] Usuario eligió login tradicional');
        this.activeLoginMethod = 'standard';
        this.passkeyError = null;
    }

    /**
     * Cambia al método de login con passkey
     */
    switchToPasskeyLogin(): void {
        this.logger.log('🔄 [LOGIN-PASSKEY] Usuario eligió login con passkey');
        this.activeLoginMethod = 'passkey';
        this.passkeyError = null;
    }

    /**
     * Verifica si el usuario tiene passkey y hace login automáticamente
     * Este método se ejecuta cuando el usuario hace click en el botón de huella
     */
    async checkAndLoginWithPasskey(): Promise<void> {
        const email = this.signinForm.get('username')?.value;

        this.logger.log('🔵 [LOGIN-PASSKEY] ========================================');
        this.logger.log('🔵 [LOGIN-PASSKEY] Usuario hizo click en botón de passkey');
        this.logger.log('📧 [LOGIN-PASSKEY] Email ingresado');

        // Validar que el email sea válido
        if (!email || this.signinForm.get('username')?.invalid) {
            this.logger.log('⚠️ [LOGIN-PASSKEY] Email inválido o vacío');
            this.toast.showError(this.translate.instant('login.messages.emailInvalidMessage'));
            return;
        }

        if (this.isLoadingPasskey) return;
        const operation = this.startPasskeyOperation();
        const generation = this.passkeyGeneration;
        this.isCheckingPasskey = true;
        this.passkeyError = null;

        try {
            // Verificar si tiene passkey
            this.logger.log('🔍 [LOGIN-PASSKEY] Verificando si el usuario tiene passkey...');
            const hasPasskey = await this.passkeyService.hasPasskey(email, null as any, operation);
            if (!this.isCurrentPasskeyOperation(generation, email)) return;

            if (!hasPasskey) {
                this.passkeyAvailabilityState = 'absent';
                this.logger.log('ℹ️ [LOGIN-PASSKEY] Usuario NO tiene passkey registrada');
                this.toast.showWarning(this.translate.instant('login.messages.noPasskeyRegistered'));
                return;
            }

            this.logger.log('✅ [LOGIN-PASSKEY] Usuario tiene passkey - Iniciando login automáticamente');
            this.hasPasskey = true;
            this.passkeyAvailabilityState = 'available';
            
            // Obtener nombre del método biométrico
            this.biometricMethodName = await this.webAuthnService.getBiometricMethodName();
            if (!this.isCurrentPasskeyOperation(generation, email)) return;
            this.logger.log('   → Método biométrico:', this.biometricMethodName);

            // Ejecutar login con passkey automáticamente
            await this.loginWithPasskey(operation);

        } catch (error: unknown) {
            if (!this.isCurrentPasskeyOperation(generation, email)) return;
            this.logger.error('❌ [LOGIN-PASSKEY] Error verificando passkey');
            this.passkeyAvailabilityState = 'error';
            const result = this.passkeyErrorHandler.handlePasskeyError(error, 'login');
            this.passkeyError = result.message;
            this.toast.showError(result.message);
        } finally {
            if (this.isCurrentPasskeyOperation(generation, email)) {
                this.isCheckingPasskey = false;
                if (!this.isLoadingPasskey) this.passkeyAbortController = null;
            }
        }
    }

    private startPasskeyOperation(): { signal: AbortSignal; deadlineAt: number } {
        this.cancelPasskeyOperation();
        this.passkeyAbortController = new AbortController();
        this.passkeyGeneration++;
        return {
            signal: this.passkeyAbortController.signal,
            deadlineAt: Date.now() + this.passkeyOperationTimeoutMs
        };
    }

    private cancelPasskeyOperation(): void {
        this.passkeyGeneration++;
        this.passkeyAbortController?.abort();
        this.passkeyAbortController = null;
        this.isCheckingPasskey = false;
        this.isLoadingPasskey = false;
        this.cancelPasskeyLookup();
    }

    private startPasskeyLookup(): { signal: AbortSignal; deadlineAt: number } {
        this.cancelPasskeyLookup();
        this.passkeyLookupAbortController = new AbortController();
        this.passkeyLookupGeneration++;
        return {
            signal: this.passkeyLookupAbortController.signal,
            deadlineAt: Date.now() + this.passkeyOperationTimeoutMs
        };
    }

    private cancelPasskeyLookup(): void {
        this.passkeyLookupGeneration++;
        this.passkeyLookupAbortController?.abort();
        this.passkeyLookupAbortController = null;
        this.isCheckingPasskey = false;
    }

    private isCurrentPasskeyLookup(generation: number, email: string): boolean {
        return generation === this.passkeyLookupGeneration &&
            !this.passkeyLookupAbortController?.signal.aborted &&
            this.signinForm.get('username')?.value === email;
    }

    private isCurrentPasskeyOperation(generation: number, email: string): boolean {
        return generation === this.passkeyGeneration &&
            !this.passkeyAbortController?.signal.aborted &&
            this.signinForm.get('username')?.value === email;
    }

}
